import React, { useState } from "react";
import { JSONViewer } from "./JSONViewer";
import { ContentCard } from "./ContentCard";
import { EmptyState } from "./EmptyState";

const jsonViewToggleStyles = {
  toggleButton:
    "flex items-center gap-2 px-2 py-1 text-xs font-medium text-muted-foreground bg-muted hover:bg-accent rounded transition-colors",
} as const;

interface JsonViewToggleProps {
  /** Raw data to display in JSON view */
  data: Record<string, any> | null | undefined;
  /** Whether structured/formatted content is available */
  hasStructured: boolean;
  /** Formatted content to display when not showing raw JSON */
  formattedContent: React.ReactNode;
  /** Section title (e.g. "Trace") */
  title?: string;
  /** Message when no structured content is available */
  emptyMessage?: string;
  /** Max height for JSON viewer, e.g. "max-h-72" */
  jsonViewMaxHeight?: string;
}

/**
 * Toggles between formatted content and raw JSON view.
 * Shows empty state with action when no structured content is available.
 */
export function JsonViewToggle({
  data,
  hasStructured,
  formattedContent,
  title = "Content",
  emptyMessage = "No structured content to display.",
  jsonViewMaxHeight = "max-h-72",
}: JsonViewToggleProps) {
  const [showRaw, setShowRaw] = useState(false);
  const displayData = data ?? {};

  const toggleButton = (
    <button
      onClick={() => setShowRaw(!showRaw)}
      className={jsonViewToggleStyles.toggleButton}
    >
      <span className="font-mono">{"{}"}</span>
      {showRaw ? "View formatted" : "View JSON"}
    </button>
  );

  return (
    <ContentCard title={title} action={toggleButton}>
      {showRaw ? (
        <JSONViewer data={displayData} maxHeight={jsonViewMaxHeight} />
      ) : hasStructured ? (
        formattedContent
      ) : (
        <EmptyState
          message={emptyMessage}
          size="xs"
          actionLabel="View raw JSON →"
          onAction={() => setShowRaw(true)}
        />
      )}
    </ContentCard>
  );
}
